import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
	COMPARISON_MODE,
	ComparisonState,
	DEFAULT_COMPARISON_STATE,
	resolveComparisonRange,
} from '../utils/AnalyticsComparisonUtils';

type UseAnalyticsBreakdownComparisonParams = {
	filters: Record<string, any>;
	fetchComparisonData: (params: Record<string, any>) => Promise<any[]>;
	normalizeComparisonData?: (rawData: any[]) => any[];
	/** Changes when client-side filters (search, tree selection) change so cached comparison data re-normalizes. */
	localFilterRevision?: string | number;
};

export function useAnalyticsBreakdownComparison({
	filters,
	fetchComparisonData,
	normalizeComparisonData,
	localFilterRevision,
}: UseAnalyticsBreakdownComparisonParams) {
	const [comparisonState, setComparisonState] = useState<ComparisonState>(DEFAULT_COMPARISON_STATE);
	const [comparisonRows, setComparisonRows] = useState<any[]>([]);
	const [comparisonLoading, setComparisonLoading] = useState(false);
	const requestIdRef = useRef(0);
	const rawComparisonDataRef = useRef<any[]>([]);

	const applyComparisonNormalization = useCallback(
		(rawData: any[]) => {
			rawComparisonDataRef.current = rawData || [];
			return normalizeComparisonData
				? normalizeComparisonData(rawComparisonDataRef.current)
				: rawComparisonDataRef.current;
		},
		[normalizeComparisonData]
	);

	const selectedStart = filters && filters.startDate ? moment(filters.startDate) : null;
	const selectedEnd = filters && filters.endDate ? moment(filters.endDate) : null;

	const hasDateRange = !!(
		selectedStart &&
		selectedEnd &&
		selectedStart.isValid() &&
		selectedEnd.isValid()
	);

	const comparisonRange = hasDateRange
		? resolveComparisonRange(selectedStart, selectedEnd, comparisonState)
		: null;

	const comparisonDisabled = !hasDateRange;
	const comparisonDisabledReason = 'Set a date range filter to enable comparison.';

	const loadComparison = useCallback(
		(state: ComparisonState) => {
			if (!state || state.mode === COMPARISON_MODE.NONE) {
				rawComparisonDataRef.current = [];
				setComparisonRows([]);
				setComparisonLoading(false);
				return;
			}
			if (!hasDateRange || !selectedStart || !selectedEnd) {
				// Invalidate any in-flight request so its (now stale) response can't write rows back
				// after the date range was cleared, and drop the spinner that would otherwise hang.
				requestIdRef.current += 1;
				rawComparisonDataRef.current = [];
				setComparisonRows([]);
				setComparisonLoading(false);
				return;
			}
			const range = resolveComparisonRange(selectedStart, selectedEnd, state);
			if (!range) {
				requestIdRef.current += 1;
				rawComparisonDataRef.current = [];
				setComparisonRows([]);
				setComparisonLoading(false);
				return;
			}

			const requestId = requestIdRef.current + 1;
			requestIdRef.current = requestId;
			setComparisonLoading(true);

			const comparisonParams = {
				...filters,
				startDate: range[0],
				endDate: range[1],
			};

			fetchComparisonData(comparisonParams)
				.then((rawData) => {
					if (requestIdRef.current !== requestId) return;
					setComparisonRows(applyComparisonNormalization(rawData || []));
				})
				.catch(() => {
					if (requestIdRef.current !== requestId) return;
					rawComparisonDataRef.current = [];
					setComparisonRows([]);
				})
				.finally(() => {
					if (requestIdRef.current !== requestId) return;
					setComparisonLoading(false);
				});
		},
		[
			filters,
			fetchComparisonData,
			applyComparisonNormalization,
			hasDateRange,
			selectedStart,
			selectedEnd,
		]
	);

	const setComparison = useCallback(
		(nextState: ComparisonState) => {
			setComparisonState(nextState);
			if (!nextState || nextState.mode === COMPARISON_MODE.NONE) {
				requestIdRef.current += 1;
				rawComparisonDataRef.current = [];
				setComparisonRows([]);
				setComparisonLoading(false);
				return;
			}
			loadComparison(nextState);
		},
		[loadComparison]
	);

	useEffect(() => {
		if (comparisonState.mode === COMPARISON_MODE.NONE) return;
		loadComparison(comparisonState);
	}, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

	// Re-apply client-side normalization when local filters change (search, tree selection)
	// without refetching the previous-period API response.
	useEffect(() => {
		if (comparisonState.mode === COMPARISON_MODE.NONE) return;
		if (rawComparisonDataRef.current.length === 0) return;
		setComparisonRows(applyComparisonNormalization(rawComparisonDataRef.current));
	}, [applyComparisonNormalization, comparisonState.mode, localFilterRevision]);

	useEffect(() => {
		if (hasDateRange) return;
		// Date range was removed entirely — reset comparison and tear down any pending request so a
		// late response can't repopulate rows and the spinner doesn't get stuck.
		requestIdRef.current += 1;
		rawComparisonDataRef.current = [];
		setComparisonState(DEFAULT_COMPARISON_STATE);
		setComparisonRows([]);
		setComparisonLoading(false);
	}, [hasDateRange]);

	return {
		comparisonState,
		setComparison,
		comparisonRows,
		comparisonLoading,
		selectedStart,
		selectedEnd,
		comparisonRange,
		comparisonDisabled,
		comparisonDisabledReason,
		hasComparison: comparisonState.mode !== COMPARISON_MODE.NONE,
	};
}
